<template>
  <card id="gatewayHealth"
        :title="$t('g-health:health')"
        :disabled="!$store.getters.bscFeature(gateway, 'control')"
        no-footer
        no-padding
  >
    <small v-if="lastHealthDate" slot="card-title-small" class="text-muted">{{ lastHealthDate - Date.now() | duration('suffix') }}</small>

    <div slot="card-body">
      <table class="table table-sm mb-0">
        <tbody>
          <tr v-for="healthField in healthFields" :key="healthField.i18nKey">
            <td>{{ $t('g-health:' + healthField.i18nKey) | capitalize }}</td>

            <template v-if="health && health[healthField.current] !== undefined && health[healthField.current] !== null">
              <td v-if="healthField.unit === '%'">
                <div class="progress">
                  <div :class="['progress-bar', 'bg-' + getColor(healthField)]" :style="{width: health[healthField.current] + '%'}" />
                </div>
              </td>
              <td :class="'text-' + getColor(healthField)">{{ health[healthField.current] }} {{ healthField.unit }}</td>
              <td v-if="healthField.unit !== '%'" />
            </template>

            <template v-if="!health || health[healthField.current] === undefined || health[healthField.current] === null">
              <td>-</td>
              <td />
            </template>
          </tr>

          <tr v-if="gateway.connection === true">
            <td>{{ $t('g-health:uptime') | capitalize }}</td>
            <td class="text-success">{{ systemUptimeDelay }}</td>
            <td />
          </tr>
          <tr v-else-if="gateway.connection === false">
            <td>{{ $t('g-health:downtime') | capitalize }}</td>
            <td class="text-danger">{{ systemDowntimeDelay }}</td>
            <td />
          </tr>
          <tr v-else>
            <td>{{ $t('g-health:uptime') | capitalize }}</td>
            <td class="text-muted">-</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  </card>
</template>

<script>
import { isEmpty } from 'lodash-es'
import to from '@/modules/await-to-js'
import Duration from 'duration-js'

const healthFields = [{
  i18nKey: 'cpu',
  current: 'cpuCurrent',
  alarmThreshold: 'cpuAlarmThreshold',
  alarmHysteresis: 'cpuAlarmHysteresis',
  unit: '%'
}, {
  i18nKey: 'ram',
  current: 'ramCurrent',
  alarmThreshold: 'ramAlarmThreshold',
  alarmHysteresis: 'ramAlarmHysteresis',
  unit: '%'
}, {
  i18nKey: 'systemDisk',
  current: 'diskSystemVolumeCurrent',
  alarmThreshold: 'diskSystemAlarmThreshold',
  alarmHysteresis: 'diskSystemAlarmHysteresis',
  unit: '%'
}, {
  i18nKey: 'userDisk',
  current: 'diskUserVolumeCurrent',
  alarmThreshold: 'diskUserAlarmThreshold',
  alarmHysteresis: 'diskUserAlarmHysteresis',
  unit: '%'
}, {
  i18nKey: 'rssi',
  current: 'rssiCurrent',
  alarmThreshold: 'rssiAlarmThreshold',
  alarmHysteresis: 'rssiAlarmHysteresis',
  unit: 'dBm'
}, {
  i18nKey: 'temperature',
  current: 'temperatureCurrent',
  alarmThreshold: 'temperatureAlarmThreshold',
  alarmHysteresis: 'temperatureAlarmHysteresis',
  unit: '°C'
}]

export default {
  props: {
    gateway: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      getting: false,

      health: null,
      refreshTimeout: null,
      healthNbPolling: 0,
      lastHealthDate: null,
      systemUptimeDelay: '',
      systemDowntimeDelay: ''
    }
  },

  computed: {
    healthFields () { return healthFields }
  },

  watch: {
    gateway (newGateway, oldGateway) {
      // Do not refresh is we are olready getting data
      if (this.getting) return

      // Reset polling if gateway connection status change
      if (newGateway.eui === oldGateway.eui && newGateway.connection !== oldGateway.connection) {
        this.health = null
        this.healthNbPolling = 0

        this.getHealth()
        return
      }

      // Do not reset if gateway id does not change
      if (isEmpty(this.gateway) || newGateway.eui === oldGateway.eui) return

      // Reset when gateway change
      this.health = null
      clearTimeout(this.refreshTimeout)
      this.refreshTimeout = null
      this.healthNbPolling = 0
      this.systemUptimeDelay = ''
      this.systemDowntimeDelay = ''

      this.getHealth()
    }
  },

  created () {
    this.getHealth()
  },

  destroyed () {
    clearTimeout(this.refreshTimeout)
  },

  methods: {
    async getHealth () {
      if (isEmpty(this.gateway)) return

      if (this.gateway.connection === true) {
        // for connected gateway, get health and up time delay from control
        this.getting = true

        let error, response
        [error, response] = await to(this.$api.getGatewayControl(this.gateway, { defaultErrorMessage: false }))

        if (error || !response) {
          this.getting = false
          this.restartHealthPolling()
          return
        }

        this.health = response.data
        this.lastHealthDate = Date.now()
        this.systemUptimeDelay = new Duration(response.data.systemUptime).humanize()
        this.getting = false
        this.restartHealthPolling()
      } else if (this.gateway.connection === false) {
        // for disconnected gateway, get the system down time delay from the last connection
        let error, response
        [error, response] = await to(this.$api.getGatewayLastConnection(this.gateway))

        if (error || !response) {
          this.restartHealthPolling()
          return
        }

        this.health = null
        this.lastHealthDate = Date.now()
        // No limit needed when gateway is disconnected
        this.healthNbPolling = 0
        this.systemDowntimeDelay = response.data.startDate
          ? new Duration(Date.now() - response.data.startDate).humanize()
          : null
      }
    },

    restartHealthPolling () {
      this.healthNbPolling++

      // In case the promise ended after the destruction of the component
      // Check the component status to only relaunch the polling when component is not destroyed
      if (this._isDestroyed || this._isBeingDestroyed) return

      // Only poll 10 times to not use too much gateway dataif it is connected
      if (this.gateway.connection === true && this.healthNbPolling > 10) return

      this.refreshTimeout = setTimeout(() => { this.getHealth() }, this.$store.getters.healthRefresh)
    },

    getColor (healthField) {
      // RSSI in negative, invert colors
      if (healthField.i18nKey === 'rssi') {
        return this.health[healthField.current] <= this.health[healthField.alarmThreshold]
          ? this.health[healthField.current] <= this.health[healthField.alarmThreshold] - this.health[healthField.alarmHysteresis]
            ? 'danger'
            : 'warning'
          : 'success'
      }

      return this.health[healthField.current] >= this.health[healthField.alarmThreshold] - this.health[healthField.alarmHysteresis]
        ? this.health[healthField.current] >= this.health[healthField.alarmThreshold]
          ? 'danger'
          : 'warning'
        : 'success'
    }
  }
}
</script>

<style lang="scss">
  // Scope does not work as it's a component style
  #gatewayHealth .card-body {
    // 223px to display 6 lines and a space for horizontal scrollbar
    height: 223px;

    .table td {
      line-height: 20px;
      vertical-align: middle;
    }
  }
</style>
